// Persists a document edit. The caller resolves contentType (block-editor flag
// or an override) and passes the value in; this core never reads a flag.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import { DocumentGenType, type ContentType } from '@heidi/schema/ml-scribe';

import {
  GET_DOCUMENT_BY_ID,
  GET_DOCUMENTS_BY_ID,
  UPDATE_DOCUMENT_BY_ID,
  type QueryDocumentResponse,
} from '../../contracts';
import { updateDocument } from '../api/documents';

/** An attempt opened at dispatch; the mutation settles it once. Mirrors the app's SaveRequestAttempt. */
type DocumentSaveAttempt = {
  succeed: (result: unknown) => void;
  fail: (error: unknown) => void;
};

type UpdateDocumentInput<TAttempt> = {
  content: string;
  sessionId: string;
  documentId: string;
  /** Set by an autosave cycle: the request emits a document_update attempt. */
  autosaveAttempt?: TAttempt;
};

export function useMutateUpdateSyncDocumentById<TAttempt>({
  contentType,
  openAttempt,
  onUpdateError,
}: {
  contentType: ContentType;
  openAttempt?: (attempt: TAttempt) => DocumentSaveAttempt;
  onUpdateError?: (
    error: Error,
    variables: { sessionId: string; documentId: string }
  ) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [UPDATE_DOCUMENT_BY_ID],
    mutationFn: async ({
      content,
      sessionId,
      documentId,
    }: UpdateDocumentInput<TAttempt>) => {
      const document = queryClient.getQueryData<QueryDocumentResponse>([
        GET_DOCUMENT_BY_ID,
        { sessionId, documentId },
      ]);

      return updateDocument({
        sessionId,
        documentId,
        name: document?.name || '',
        content,
        generationType: document?.generation_type || DocumentGenType.TEMPLATE,
        updatedDictation: document?.updated_dictation || '',
        contentType,
      });
    },
    onMutate: async ({ sessionId, documentId, content, autosaveAttempt }) => {
      await queryClient.cancelQueries({
        queryKey: [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
      });

      const previousDocument = queryClient.getQueryData<QueryDocumentResponse>([
        GET_DOCUMENT_BY_ID,
        { sessionId, documentId },
      ]);

      queryClient.setQueryData<QueryDocumentResponse>(
        [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }

          return produce(oldData, (draft) => {
            draft.content = content;
            draft.content_type = contentType;
          });
        }
      );

      // Opened here, before the mutation can pause offline, so the wait counts.
      const attempt =
        autosaveAttempt && openAttempt ? openAttempt(autosaveAttempt) : undefined;
      return { previousDocument, attempt };
    },
    onSuccess: async (data, { sessionId, documentId }, context) => {
      // Guarded: a context restored from persistence has lost its methods.
      context?.attempt?.succeed?.(data);

      await queryClient.invalidateQueries({
        queryKey: [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
      });

      await queryClient.invalidateQueries({
        queryKey: [GET_DOCUMENTS_BY_ID, { sessionId }],
      });
    },
    onError: (error, { sessionId, documentId }, context) => {
      context?.attempt?.fail?.(error);
      onUpdateError?.(error, { sessionId, documentId });
    },
  });
}
